/**
 * A line's requested-delivery-date picker, plus the "Values have been updated" popper shown when
 * a picked or stored date was moved onto an allowed delivery date.
 *
 * Position: rendered by the delivery-date cells of the cart
 * (`ShoppingCart/ShoppingCartTable/ShoppingCartTableDeliveryDateCell.tsx`), checkout
 * (`Shared/CheckoutOrderDetails/CheckoutOrderDetailsTable/Helper/CheckoutOrderDetailsTableDeliveryDateCell.tsx`),
 * quote edit (`QuoteDetails/Helpers/EditProductsTableComponents/TableRequestedDeliveryDate.tsx`),
 * Easy Quote (`EasyQuote/Helper/TableComponents/EasyQuoteTableDeliveryDateCell.tsx`) and inside
 * the future-dates modal (`Shared/FutureDates/FutureDatesDesktop.tsx` / `FutureDatesMobile.tsx`,
 * with `isFutureDates` and an `externalRef`). The picker's start date and range come from
 * `Helper/DeliveryDateAndMinMaxRange.ts` → `getDeliveryDateAndValidDateRange` on the line's
 * slowest plant. The caller's `handleOnDeliveryDateChange` persists the date.
 *
 * Rules a change must keep:
 * - A picked date that is not an allowed delivery date is replaced, before the callback, by the
 *   first allowed date after it ({@link isValidFutureDate}, which reads the first lead-time
 *   day's list). Weekends are disabled in the picker.
 * - The popper must survive a remount: a date change makes the caller re-fetch items and
 *   delivery times, which remounts this component. The pending notice is kept in
 *   `sessionStorage` under `dateAdjustmentNotification_<rowData.id>`, shown on the next mount,
 *   removed once shown, and all such keys are removed on any route change.
 *
 * Related: `Shared/FutureDates/FutureDatesTrigger.tsx` (same popper for multi-date lines), and
 * the copies of {@link getPopperColor} in
 * `Shared/CheckoutOrderDetails/CheckoutOrderDetailsTable/Helper/CheckoutOrderDisplayDateAsText.tsx`
 * and `EasyQuote/Helper/TableComponents/EasyQuoteDisplayDateAsText.tsx`.
 */
import React, { MutableRefObject, useEffect } from "react";
import {
	DraftQuoteLineItem,
	getMaxLeadTimeDay,
	ShoppingCartDataType,
} from "@ryerson/frontend.common";
import { DatePicker } from "@ryerson/frontend.form-controls";
import { Alert } from "@ryerson/frontend.notification";
import { Popper } from "@ryerson/frontend.common";
import styled from "@emotion/styled";
import { getDeliveryDateAndValidDateRange } from "@components/Helper/DeliveryDateAndMinMaxRange";
import { Typography } from "@ryerson/frontend.typography";
import { LanguageContent } from "@ryerson/frontend.common";
import { useApplication } from "@ryerson/frontend.application";
import { ITheme, useTheme } from "@ryerson/frontend.theme";
import { IDeliveryTimesResponse, ILeadTimeDay } from "@ryerson/common.models";
import { parseDateSafely } from "@components/Helper/common";
import {
	DateFormat,
	ensureDatePickerCloses,
	findFirstDateThatCompares,
	formatDateToString,
} from "@components/Helper/utilities";

import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { useRouter } from "next/router";

interface PopperLabels {
	quantityRecalculatedNotify: string;
}
/**
 * "Values have been updated" in en / fr / es; also read by
 * `Shared/FutureDates/FutureDatesTrigger.tsx`.
 */
export const popperContent: LanguageContent<PopperLabels> = {
	en: { quantityRecalculatedNotify: "Values have been updated" },
	fr: { quantityRecalculatedNotify: "Les valeurs ont été mises à jour" },
	es: { quantityRecalculatedNotify: "Los valores han sido actualizados" },
};

/** Theme colour for the notification popper's text (white), background (dark gray) or border. */
export const getPopperColor = (whichElType: "text" | "background" | "border", theme: ITheme) => {
	const { white, lightGray, darkGray } = theme.colors.primary;
	return {
		text: white,
		background: darkGray,
		border: lightGray,
	}[whichElType];
};

interface DatePickerPopperProps {
	id: number;
	rowData: DraftQuoteLineItem | ShoppingCartDataType;
	isMobile: boolean;
	leadTimeAndDeliveryDates: IDeliveryTimesResponse | undefined;
	isFetching: boolean;
	/** Receives the (possibly snapped) date, `rowData`, and `id` as the third argument. */
	handleOnDeliveryDateChange: (date: Date, rowData: any, rowIndex: number) => void;
	/** Set by the caller when it corrected this line's stored date; shows the popper once. */
	displayValuesUpdated?: boolean;
	setDisplayValuesUpdate?: React.Dispatch<React.SetStateAction<boolean>>;
	/** Anchor element owned by a parent (the future-dates modal); the local anchor is not used. */
	externalRef?: React.MutableRefObject<HTMLDivElement | null | undefined>;
	/**
	 * Allow passing the popperDisplayed flag for use inside modal
	 */
	popperDisplayedState?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
	/**
	 * Set inside the future-dates modal. Passed to `getDeliveryDateAndValidDateRange` as
	 * `useAllowedDeliveryDates`, so the picker's range ends at the last allowed date. The picker
	 * is given only this min/max range (weekends disabled), not the dates every selected line
	 * allows, so it offers dates one line skips (a known defect; see
	 * `FutureDates/FutureDatesHelpers.tsx` → `getFilteredDeliveryTimes`).
	 */
	isFutureDates?: boolean;
	/** Show notification immediately when no component remount will occur (e.g., Express Checkout) */
	showImmediateNotification?: boolean;
	/** Called when the date-adjustment notification banner closes (2s auto or click) */
	onNotificationClosed?: () => void;
}

/**
 * Snaps a picked `MM/DD/YYYY` date onto the allowed delivery dates of the first lead-time day:
 * returns the same date when it is allowed, else the first allowed date after it, else
 * `undefined` (picked date after the last allowed one). Returns the picked date unchanged when
 * there are no allowed dates. Assumes the list is sorted ascending.
 */
export function isValidFutureDate(
	leadTimeAndDeliveryDates: IDeliveryTimesResponse | undefined,
	dateStringSelected: string
) {
	let { allowedDeliveryDates = [] } = (leadTimeAndDeliveryDates?.leadTimeDays[0] ||
		{}) as ILeadTimeDay & { allowedDeliveryDates: string[] };
	let validatedDate: string | undefined = dateStringSelected;

	if (!!allowedDeliveryDates && allowedDeliveryDates.length > 0) {
		// This function attempts find the first  date (in array) that is equal to selected date,
		// if cant find, then looks for first date greater than the selected date
		validatedDate = findFirstDateThatCompares(
			dateStringSelected,
			allowedDeliveryDates,
			"equal",
			"greater"
		);
	}
	return validatedDate;
}

/**
 * Requested-delivery-date picker for one line; see the file header. The popper closes itself
 * after 2 s or on click, calling `onNotificationClosed`.
 */
export const DatePickerPopper: React.FC<DatePickerPopperProps> = ({
	id,
	rowData,
	isMobile,
	leadTimeAndDeliveryDates,
	isFetching,
	handleOnDeliveryDateChange,
	displayValuesUpdated,
	setDisplayValuesUpdate,
	externalRef,
	popperDisplayedState,
	isFutureDates,
	showImmediateNotification,
	onNotificationClosed,
}) => {
	const router = useRouter();
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();
	const { flexibleDelivery } = useEcommerce();

	let triggerRef = React.useRef<HTMLDivElement>();
	let refToUse: MutableRefObject<HTMLDivElement | null | undefined>;

	if (externalRef) {
		refToUse = externalRef;
	} else {
		refToUse = triggerRef;
	}

	// State-tracked anchor element — drives Popper mount so #tool-tip exists in DOM
	// before isDisplaying=true, preventing the popper.js "Invalid reference" warning.
	const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

	const initValueForIsPopperDisplayedState: boolean =
		(typeof popperDisplayedState !== "undefined" && popperDisplayedState[0]) || false;
	const [isPopperDisplayed, setIsPopperDisplayed] = React.useState<boolean>(
		initValueForIsPopperDisplayedState
	);

	const PopperContent = styled.div`
		width: 240px;
	`;

	let deliveryDate = {} as ILeadTimeDay;
	if (leadTimeAndDeliveryDates) {
		let leadTimeDays = leadTimeAndDeliveryDates?.leadTimeDays || [];
		if (leadTimeDays?.length > 0) {
			deliveryDate = getMaxLeadTimeDay(leadTimeDays);
		}
	}

	let { startDate, minDate, maxDate } = getDeliveryDateAndValidDateRange(
		true,
		flexibleDelivery,
		deliveryDate,
		rowData?.requestedDeliveryDate,
		isFutureDates
	);

	/* Beginning logic for Popper Notification display */
	// support of popper notification displays after a remount of the DatePickerPopper component
	// after an items call onchange (which will always remount the this component)
	const notificationKey = `dateAdjustmentNotification_${rowData.id}`;

	/* Check for pending notifications on mount/remount (after onchange) */
	React.useEffect(() => {
		const shouldShowNotification = sessionStorage.getItem(notificationKey);
		if (shouldShowNotification === "true") {
			// Small delay to ensure DOM ref is ready before showing popper
			setTimeout(() => {
				if (refToUse.current?.isConnected) {
					setIsPopperDisplayed(true);
				}
			}, 200);
		}
	}, []);

	/* Clean up session storage when popper notification actually gets displayed */
	React.useEffect(() => {
		if (isPopperDisplayed) {
			// Clean up the session storage flag since notification is now showing
			sessionStorage.removeItem(notificationKey);
			setTimeout(() => {
				setIsPopperDisplayed(false);
				onNotificationClosed?.();
			}, 2000);
		}
	}, [isPopperDisplayed]);

	/* Note: Handle external trigger to show popper (from parent component) */
	React.useEffect(() => {
		if (displayValuesUpdated && refToUse.current?.isConnected) {
			setIsPopperDisplayed(true);
		}
		if (setDisplayValuesUpdate) {
			setDisplayValuesUpdate(false);
		}
	}, [displayValuesUpdated]);

	/* Note: Cleanup session storage on route change (navigating away) for (all) popper notification flags
	this ensures no stale notifications appear when returning to the page/component */
	React.useEffect(() => {
		const handleRouteChangeStart = () => {
			clearSessionStorageByPattern("dateAdjustmentNotification_");
		};
		router.events.on("routeChangeStart", handleRouteChangeStart);
		return () => {
			router.events.off("routeChangeStart", handleRouteChangeStart);
		};
	}, [router]);

	// Removes every sessionStorage key that contains `pattern`, for all rows, not just this one.
	function clearSessionStorageByPattern(pattern: string) {
		Object.keys(sessionStorage).forEach((key) => {
			if (key.includes(pattern)) {
				sessionStorage.removeItem(key);
			}
		});
	}

	/* End logic for Popper Notification display */

	const content = popperContent[language || "en"];

	const setRefToUse = React.useCallback((node: HTMLDivElement | null) => {
		refToUse.current = node;
		setAnchorEl(node);
	}, []);

	const popperTargetEl = (externalRef ? externalRef.current : anchorEl) ?? null;
	return (
		// don’t use the local ref if we’re in a modal
		<div ref={!!externalRef ? undefined : setRefToUse}>
			<DatePicker
				required={false}
				disableWeekends={true}
				size={isMobile ? "md" : "sm"}
				type={!isMobile ? "tertiary" : "primary"}
				onChange={(date) => {
					const dateStringSelected = formatDateToString(date, DateFormat["MM/DD/YYYY"]);
					const validatedDate = isValidFutureDate(
						leadTimeAndDeliveryDates,
						dateStringSelected
					);
					// The range above comes from the slowest plant; the snap list from
					// leadTimeDays[0]. When nothing is later (validatedDate undefined), the
					// picked date is kept but still flagged as adjusted below.
					const returnDate =
						validatedDate && validatedDate !== dateStringSelected
							? parseDateSafely(validatedDate) || date
							: date;

					ensureDatePickerCloses();

					// Determine if Popper Notification should be displayed (after remount)
					if (validatedDate !== dateStringSelected) {
						// Flag that we need to show notification after potential remount
						sessionStorage.setItem(notificationKey, "true");

						// Show immediate notification if specified (e.g., Express Checkout with no remount)
						if (showImmediateNotification && refToUse.current?.isConnected) {
							setIsPopperDisplayed(true);
							// Clean up session storage since we're showing immediately
							setTimeout(() => {
								clearSessionStorageByPattern("dateAdjustmentNotification_");
							}, 100);
						}
					}
					//callback
					handleOnDeliveryDateChange(returnDate, rowData, id);
				}}
				language={language}
				minDate={minDate}
				maxDate={maxDate}
				startDate={!!startDate ? new Date(startDate) : null}
				disabled={isFetching}
			/>
			{!!popperTargetEl && (
				<Popper
					{...{
						id: `notifier-popper-${id}`,
						targetEl: popperTargetEl,
						backColor: getPopperColor("background", theme),
						offsetY: isMobile && externalRef ? -20 : 10,
						offsetX: isMobile && externalRef ? -50 : 0,
						borderColor: getPopperColor("border", theme),
						placement: isMobile ? "bottom-end" : "bottom-start",
						isDisplaying: isPopperDisplayed,
						onClick: () => {
							setIsPopperDisplayed(false);
							onNotificationClosed?.();
						},
					}}
				>
					<>
						<PopperContent>
							<Alert size="sm" severity={"info"}>
								<Typography color={getPopperColor("text", theme)}>
									{content.quantityRecalculatedNotify}
								</Typography>
							</Alert>
						</PopperContent>
					</>
				</Popper>
			)}
		</div>
	);
};
